import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { Observable } from 'rxjs';
import { ListingInfo } from './listing-api.types';
import { Position } from 'geojson';
import { ExternalEntity } from './types/external-entities.types';
import { BasikFeatureLayer, LayersEnableMap } from '../../../pages/dp-map/utils/types';
import { MAPBOX_MAP_STYLE } from '../../../pages/dp-map/utils/consts';

export type DetailedPlotSettings = {
  isShowDetailedDimensions: boolean;
  isShowNorthAngle: boolean;
  isShowBoundaries: boolean;
  parcelFollowingEnabled: boolean;
};
export type DashboardBoard = {
  type: 'dashboard';
  id: string;
  name: string;
  authorId: number;
  projectId: string;
  createdAt: string;
  project_access: ProjectAccess;
  rawData: {
    reportId?: string;
    reportType?: 'valuation' | 'market-analysis' | 'investment';
    reportData?: any;
  };
  users: BoardUser[];
  sharedLink: {
    accessLevel: BoardShareLinkAccessLevel | null;
    id: null | string;
  };
};

export type Board = MapBoard | ListingBoard | DashboardBoard;

export type BoardUser = {
  id: number;
  name: string;
  avatar: string;
  guest: boolean;
};

export type ProjectAccess = {
  balance: number;
  subscription_type: string;
  isFree: boolean;
  isStandart: boolean;
  isProfessional: boolean;
  isEnterprise: boolean;
  data: {
    data: boolean;
    elevation: boolean;
    hazardProne: boolean;
    landRights: boolean;
    overlapping: boolean;
    planningInformation: boolean;
    secondaryZoningOverlay: boolean;
    roadAccessibility: boolean;
    polygonDetails: boolean;
    positioning: boolean;
    landDetails: boolean;
  };
  market: {
    market: boolean;
    graphAccess: boolean;
  };
  ai: {
    report: boolean;
    availableTokenAmount: number;
    aiFreeToken: number;
  };
};

export type MapBoard = {
  type: 'map';
  authorId: number;
  createdAt: string;
  projectId: string;
  id: string;
  name: string;
  project_access: ProjectAccess;
  rawData: {
    plotUuid: string;
    centerCoordinates: Position | null;
    detailedSettings: DetailedPlotSettings | null;
    layersMap: LayersEnableMap | null;
    layersAdded: {
      [BasikFeatureLayer.Basik]: boolean;
      [BasikFeatureLayer.Rdtr]: boolean;
      [BasikFeatureLayer.Ownerships]: boolean;
      [BasikFeatureLayer.Price]: boolean;
      [BasikFeatureLayer.Heatmap]: boolean;
      [BasikFeatureLayer.Btn]: boolean;
    } | null;
    pluginsAdded: {
      reidex: boolean;
    } | null;
    style: string | null;
    initialZoom: number;
  };
  users: BoardUser[];
  sharedLink: {
    accessLevel: BoardShareLinkAccessLevel | null;
    id: null | string;
  };
};

export type ListingBoard = {
  id: string;
  name: string;
  authorId: number;
  rawData: any[];
  project_access: ProjectAccess;
  projectId: string;
  type: 'listing';
  createdAt: string;
  listing: ListingInfo;
  users: BoardUser[];
  sharedLink: {
    accessLevel: null | BoardShareLinkAccessLevel;
    id: null | string;
  };
};

export type BoardShareLinkAccessLevel = 'editor' | 'viewer' | 'no-access';

@Injectable({
  providedIn: 'root',
})
export class BoardsApiService {
  private _baseUrl: string = `${environment.apiUrl}/api`;
  constructor(private http: HttpClient) {}

  public getBoardsList(): Observable<Board[]> {
    return this.http.get<Board[]>(`${this._baseUrl}/board/list`);
  }

  public getProjectBoardsList(projectId: string): Observable<Board[]> {
    return this.http.get<Board[]>(`${this._baseUrl}/${projectId}/board/list`);
  }

  public getById(id: string): Observable<Board> {
    return this.http.get<Board>(`${this._baseUrl}/board/list/${id}`);
  }

  public updateBoard(board: Partial<Board>) {
    return this.http.post<Board>(`${this._baseUrl}/${board.projectId}/board/edit`, board);
  }

  public createProjectBoard(projectId: string, board: Partial<Board>) {
    return this.http.post<Board>(`${this._baseUrl}/${projectId}/board/create`, board);
  }

  public createBoardFromDefault(entities: ExternalEntity[]) {
    return this.http.post<{ boardId: string }>(`${this._baseUrl}/board/create-from-default`, {
      create: entities,
    });
  }

  public deleteBoard(board: Partial<Board>) {
    return this.http.post<Board>(
      `${this._baseUrl}/${board.projectId}/board/delete/${board.id}`,
      {},
    );
  }

  public joinById(id: string) {
    return this.http.get<unknown>(`${this._baseUrl}/board/join/${id}`);
  }

  public getBoardEntities(id: string) {
    return this.http.get<ExternalEntity[]>(`${this._baseUrl}/geometry/list/${id}`);
  }

  public deleteBoardEntity(id: string) {
    return this.http.post<ExternalEntity>(`${this._baseUrl}/geometry/delete`, {
      id,
    });
  }

  public getDefaultBoardEntities(): Observable<ExternalEntity[]> {
    return this.http.get<ExternalEntity[]>(`${this._baseUrl}/geometry/list/default/get`);
  }

  public getDefaultBoard(): Observable<Board> {
    return this.http.get<Board>(`${this._baseUrl}/board/default`);
  }

  public editBoardShareLinkAccessLevel(boardId: string, accessLevel: BoardShareLinkAccessLevel, projectId: string) {
    return this.http.post<{ link: string }>(`${this._baseUrl}/${projectId}/board/share-link/edit/${boardId}`, {
      accessLevel: accessLevel,
      needRecreate: false,
    });
  }
}

export const DefaultProjectAccess = {
  balance: 0,
  subscription_type: 'free',
  isFree: false,
  isStandart: false,
  isProfessional: false,
  isEnterprise: false,
  data: {
    data: false,
    elevation: false,
    hazardProne: false,
    landRights: false,
    overlapping: false,
    planningInformation: false,
    secondaryZoningOverlay: false,
    roadAccessibility: false,
    polygonDetails: false,
    positioning: false,
    landDetails: false,
  },
  market: {
    market: false,
    graphAccess: false,
  },
  ai: {
    report: false,
    availableTokenAmount: 0,
    aiFreeToken: 0,
  },
};

export const Default_Mock_Board: MapBoard = {
  id: '',
  authorId: 0,
  name: 'Untitled board',
  projectId: '',
  project_access: DefaultProjectAccess,
  rawData: {
    plotUuid: '',
    centerCoordinates: [106.80764894015772, -6.14539097732677],
    detailedSettings: {
      parcelFollowingEnabled: false,
      isShowDetailedDimensions: true,
      isShowBoundaries: true,
      isShowNorthAngle: true,
    },
    layersMap: {
      [BasikFeatureLayer.Basik]: true,
      [BasikFeatureLayer.Rdtr]: false,
      [BasikFeatureLayer.Price]: false,
      [BasikFeatureLayer.Ownerships]: false,
      [BasikFeatureLayer.Heatmap]: false,
      [BasikFeatureLayer.Btn]: false,
    },
    layersAdded: {
      [BasikFeatureLayer.Basik]: false,
      [BasikFeatureLayer.Rdtr]: false,
      [BasikFeatureLayer.Price]: false,
      [BasikFeatureLayer.Ownerships]: false,
      [BasikFeatureLayer.Heatmap]: false,
      [BasikFeatureLayer.Btn]: false,
    },
    pluginsAdded: {
      reidex: false,
    },
    style: MAPBOX_MAP_STYLE.Streets,
    initialZoom: 16,
  },
  type: 'map',
  createdAt: '2024-02-28T12:51:35+00:00',
  users: [],
  sharedLink: {
    id: null,
    accessLevel: null,
  },
};
